import axios from 'axios';
import { InAppMessageDto, CreateInAppMessageRequest, UpdateInAppMessageRequest, MessageStatsResponse } from '../types/InAppMessage';
import { 
  CommentDto, 
  CommentListResponse, 
  CommentStatsDto, 
  BulkDeleteResponse, 
  UpdateCommentDto,
  PoiTypeSettingDto,
  FeatureSettingsResponse,
  UpdateCommentViewTypeDto,
  BulkUpdateCommentViewTypeDto,
  BulkUpdateResponse
} from '../types/Comment';
import {
  MediaListResponse,
  MediaOwnerUsageDto
} from '../types/Media';
import {
  TALocalDto,
  CreateTALocalRequest,
  UpdateTALocalRequest,
  TALocalListResponse,
  TALocalStatsDto,
  ApprovalChargePreview
} from '../types/TALocal';
import {
  TidePointDto,
  CreateTidePointRequest,
  UpdateTidePointRequest,
  TidePointListResponse,
  TidePointStatsDto
} from '../types/TidePoint';
import {
  AvailableClaimDto,
  UserWithClaimsDto,
  SetClaimsRequest
} from '../types/UserClaims';
import {
  VolunteerEventDto,
  SaveVolunteerEventRequest,
  VolunteerEventListResponse,
  VolunteerEventStatus,
  RosterResponse,
  RemoveRegistrantResponse,
  ActivityResponse
} from '../types/VolunteerEvent';
import {
  TrailObservationDto,
  TrailObservationListResponse,
  TrailObservationStatsDto
} from '../types/Observation';
import { authService } from './authServiceBFF';

const BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001';

const api = axios.create({
  baseURL: BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true, // Important: send cookies with requests
});

// Add request interceptor for CSRF token and logging
api.interceptors.request.use(
  async (config) => {
    if (process.env.NODE_ENV === 'development') {
      console.debug(`Making ${config.method?.toUpperCase()} request to ${config.url}`);
    }
    
    // Add CSRF token for state-changing requests
    if (config.method && ['post', 'put', 'delete', 'patch'].includes(config.method.toLowerCase())) {
      let csrfToken = authService.getCachedCsrfToken();
      if (!csrfToken) {
        csrfToken = await authService.getCsrfToken();
      }
      if (csrfToken) {
        config.headers['X-CSRF-TOKEN'] = csrfToken;
      }
    }
    
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Add response interceptor for error handling
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    console.error('API Error:', error.response?.data || error.message);
    
    // If we get a 401, the user needs to login
    if (error.response?.status === 401) {
      // Redirect to login
      await authService.login();
    }
    
    return Promise.reject(error);
  }
);

export const inAppMessageApi = {
  // Get all messages
  getMessages: async (params?: { isActive?: boolean; messageType?: string; limit?: number }): Promise<InAppMessageDto[]> => {
    const response = await api.get<InAppMessageDto[]>('/api/InAppMessages', { params });
    return response.data;
  },

  // Get a specific message
  getMessage: async (id: string): Promise<InAppMessageDto> => {
    const response = await api.get<InAppMessageDto>(`/api/InAppMessages/${id}`);
    return response.data;
  },

  // Create a new message
  createMessage: async (message: CreateInAppMessageRequest): Promise<InAppMessageDto> => {
    const response = await api.post<InAppMessageDto>('/api/InAppMessages', message);
    return response.data;
  },

  // Update an existing message
  updateMessage: async (id: string, message: UpdateInAppMessageRequest): Promise<InAppMessageDto> => {
    const response = await api.put<InAppMessageDto>(`/api/InAppMessages/${id}`, message);
    return response.data;
  },

  // Delete a message
  deleteMessage: async (id: string): Promise<void> => {
    await api.delete(`/api/InAppMessages/${id}`);
  },

  // Get message statistics
  getStats: async (): Promise<MessageStatsResponse> => {
    const response = await api.get<MessageStatsResponse>('/api/InAppMessages/stats');
    return response.data;
  },
};

export const commentApi = {
  // Get all comments with filtering
  getComments: async (params?: {
    page?: number;
    pageSize?: number;
    featureId?: string;
    featureType?: string;
    featureName?: string;
    userId?: string;
    includeDeleted?: boolean
  }): Promise<CommentListResponse> => {
    const response = await api.get<CommentListResponse>('/api/Comments', { params });
    return response.data;
  },

  // Get a specific comment
  getComment: async (id: string): Promise<CommentDto> => {
    const response = await api.get<CommentDto>(`/api/Comments/${id}`);
    return response.data;
  },

  // Update comment content
  updateComment: async (id: string, data: UpdateCommentDto): Promise<CommentDto> => {
    const response = await api.put<CommentDto>(`/api/Comments/${id}`, data);
    return response.data;
  },

  // Delete (soft) a comment
  deleteComment: async (id: string, deletedBy: string = 'admin'): Promise<void> => {
    await api.delete(`/api/Comments/${id}`, { params: { deletedBy } });
  },

  // Restore a deleted comment
  restoreComment: async (id: string): Promise<CommentDto> => {
    const response = await api.post<CommentDto>(`/api/Comments/${id}/restore`);
    return response.data;
  },

  // Bulk delete comments by feature
  bulkDeleteByFeature: async (featureId: string, featureType?: string, deletedBy: string = 'admin'): Promise<BulkDeleteResponse> => {
    const response = await api.delete<BulkDeleteResponse>(`/api/Comments/feature/${featureId}`, { 
      params: { featureType, deletedBy } 
    });
    return response.data;
  },

  // Bulk delete comments by user
  bulkDeleteByUser: async (userId: string, deletedBy: string = 'admin'): Promise<BulkDeleteResponse> => {
    const response = await api.delete<BulkDeleteResponse>(`/api/Comments/user/${userId}`, { 
      params: { deletedBy } 
    });
    return response.data;
  },

  // Get comment statistics
  getStats: async (): Promise<CommentStatsDto> => {
    const response = await api.get<CommentStatsDto>('/api/Comments/stats');
    return response.data;
  },
};

export const commentSettingsApi = {
  // Get POI type settings
  getPoiTypeSettings: async (): Promise<PoiTypeSettingDto[]> => {
    const response = await api.get<PoiTypeSettingDto[]>('/api/CommentSettings/poi-types');
    return response.data;
  },

  // Update POI type setting
  updatePoiTypeSetting: async (id: number, data: UpdateCommentViewTypeDto): Promise<PoiTypeSettingDto> => {
    const response = await api.put<PoiTypeSettingDto>(`/api/CommentSettings/poi-types/${id}`, data);
    return response.data;
  },

  // Get feature settings
  getFeatureSettings: async (featureType: string, params?: { 
    page?: number; 
    pageSize?: number; 
    search?: string 
  }): Promise<FeatureSettingsResponse> => {
    const response = await api.get<FeatureSettingsResponse>('/api/CommentSettings/features', { 
      params: { featureType, ...params } 
    });
    return response.data;
  },

  // Update individual feature setting
  updateFeatureSetting: async (featureType: string, id: string, data: UpdateCommentViewTypeDto): Promise<void> => {
    await api.put(`/api/CommentSettings/features/${featureType}/${id}`, data);
  },

  // Bulk update feature settings
  bulkUpdateFeatures: async (data: BulkUpdateCommentViewTypeDto): Promise<BulkUpdateResponse> => {
    const response = await api.post<BulkUpdateResponse>('/api/CommentSettings/features/bulk-update', data);
    return response.data;
  },
};

export const taLocalsApi = {
  // Get all TA Locals with filtering
  getLocals: async (params?: {
    page?: number;
    pageSize?: number;
    search?: string;
    region?: string;
    category?: string;
    includeDeleted?: boolean;
  }): Promise<TALocalListResponse> => {
    const response = await api.get<TALocalListResponse>('/api/TALocals', { params });
    return response.data;
  },

  // Get a specific TA Local
  getLocal: async (id: string): Promise<TALocalDto> => {
    const response = await api.get<TALocalDto>(`/api/TALocals/${encodeURIComponent(id)}`);
    return response.data;
  },

  // Create a new TA Local
  /** What approving this listing will charge. Read-only; safe to call on open. */
  getChargePreview: async (id: string): Promise<ApprovalChargePreview> => {
    const response = await api.get<ApprovalChargePreview>(
      `/api/TALocals/${encodeURIComponent(id)}/charge-preview`
    );
    return response.data;
  },

  createLocal: async (data: CreateTALocalRequest): Promise<TALocalDto> => {
    const response = await api.post<TALocalDto>('/api/TALocals', data);
    return response.data;
  },

  // Update an existing TA Local
  updateLocal: async (id: string, data: UpdateTALocalRequest): Promise<TALocalDto> => {
    const response = await api.put<TALocalDto>(`/api/TALocals/${id}`, data);
    return response.data;
  },

  // Soft delete a TA Local
  deleteLocal: async (id: string): Promise<void> => {
    await api.delete(`/api/TALocals/${id}`);
  },

  // Restore a soft-deleted TA Local
  restoreLocal: async (id: string): Promise<TALocalDto> => {
    const response = await api.post<TALocalDto>(`/api/TALocals/${id}/restore`);
    return response.data;
  },

  // Approve a pending TA Local; charges the partner's card
  approveLocal: async (id: string): Promise<TALocalDto> => {
    const response = await api.post<TALocalDto>(`/api/TALocals/${id}/approve`);
    return response.data;
  },

  // Reject a pending TA Local
  rejectLocal: async (id: string, reason: string): Promise<TALocalDto> => {
    const response = await api.post<TALocalDto>(`/api/TALocals/${id}/reject`, { reason });
    return response.data;
  },

  // Get TA Locals statistics
  getStats: async (): Promise<TALocalStatsDto> => {
    const response = await api.get<TALocalStatsDto>('/api/TALocals/stats');
    return response.data;
  },
};

export const mediaApi = {
  /** Who is holding how much, within a scope. */
  usage: async (scope = 'partners'): Promise<MediaOwnerUsageDto[]> => {
    const response = await api.get<MediaOwnerUsageDto[]>('/api/Media/usage', { params: { scope } });
    return response.data;
  },

  search: async (params: {
    scope?: string;
    ownerId?: string;
    entityType?: string;
    entityId?: string;
    includeDeleted?: boolean;
    page?: number;
    pageSize?: number;
  }): Promise<MediaListResponse> => {
    const response = await api.get<MediaListResponse>('/api/Media', { params });
    return response.data;
  },

  /** Removes the blob immediately; the row stays, marked deleted. */
  remove: async (id: string): Promise<void> => {
    await api.delete(`/api/Media/${id}`);
  },

  /** Everything one owner holds in a scope. Returns how far it got. */
  removeForOwner: async (ownerId: string, scope = 'partners'): Promise<{ deleted: number; failed: number }> => {
    const response = await api.delete<{ deleted: number; failed: number }>(
      `/api/Media/owner/${encodeURIComponent(ownerId)}`, { params: { scope } });
    return response.data;
  },
};

export const tidePointsApi = {
  // Get all tide points with filtering
  getTidePoints: async (params?: {
    page?: number;
    pageSize?: number;
    search?: string;
    isActive?: boolean;
    includeDeleted?: boolean;
  }): Promise<TidePointListResponse> => {
    const response = await api.get<TidePointListResponse>('/api/TidePoints', { params });
    return response.data;
  },

  // Get a specific tide point
  getTidePoint: async (id: string): Promise<TidePointDto> => {
    const response = await api.get<TidePointDto>(`/api/TidePoints/${id}`);
    return response.data;
  },

  // Create a new tide point
  createTidePoint: async (data: CreateTidePointRequest): Promise<TidePointDto> => {
    const response = await api.post<TidePointDto>('/api/TidePoints', data);
    return response.data;
  },

  // Update an existing tide point
  updateTidePoint: async (id: string, data: UpdateTidePointRequest): Promise<TidePointDto> => {
    const response = await api.put<TidePointDto>(`/api/TidePoints/${id}`, data);
    return response.data;
  },

  // Refresh tide data from NIWA API
  refreshTideData: async (id: string): Promise<TidePointDto> => {
    const response = await api.post<TidePointDto>(`/api/TidePoints/${id}/refresh`);
    return response.data;
  },

  // Soft delete a tide point
  deleteTidePoint: async (id: string): Promise<void> => {
    await api.delete(`/api/TidePoints/${id}`);
  },

  // Restore a soft-deleted tide point
  restoreTidePoint: async (id: string): Promise<TidePointDto> => {
    const response = await api.post<TidePointDto>(`/api/TidePoints/${id}/restore`);
    return response.data;
  },

  // Bulk delete tide points
  bulkDeleteTidePoints: async (ids: string[]): Promise<void> => {
    const numericIds = ids.map(id => parseInt(id, 10));
    await api.post('/api/TidePoints/bulk-delete', { ids: numericIds });
  },

  // Get tide points statistics
  getStats: async (): Promise<TidePointStatsDto> => {
    const response = await api.get<TidePointStatsDto>('/api/TidePoints/stats');
    return response.data;
  },
};

export const volunteerEventsApi = {
  // Get all volunteer events with filtering
  getEvents: async (params?: {
    page?: number;
    pageSize?: number;
    search?: string;
    region?: string;
    status?: VolunteerEventStatus;
    includeDeleted?: boolean;
  }): Promise<VolunteerEventListResponse> => {
    const response = await api.get<VolunteerEventListResponse>('/api/VolunteerEvents', { params });
    return response.data;
  },

  // Get a specific volunteer event
  getEvent: async (id: string): Promise<VolunteerEventDto> => {
    const response = await api.get<VolunteerEventDto>(`/api/VolunteerEvents/${encodeURIComponent(id)}`);
    return response.data;
  },

  // Create a new volunteer event (starts as Draft)
  createEvent: async (data: SaveVolunteerEventRequest): Promise<VolunteerEventDto> => {
    const response = await api.post<VolunteerEventDto>('/api/VolunteerEvents', data);
    return response.data;
  },

  // Update an existing volunteer event
  updateEvent: async (id: string, data: SaveVolunteerEventRequest): Promise<VolunteerEventDto> => {
    const response = await api.put<VolunteerEventDto>(`/api/VolunteerEvents/${id}`, data);
    return response.data;
  },

  // Soft delete a volunteer event
  deleteEvent: async (id: string): Promise<void> => {
    await api.delete(`/api/VolunteerEvents/${id}`);
  },

  // Publish a draft/cancelled event
  publishEvent: async (id: string): Promise<VolunteerEventDto> => {
    const response = await api.post<VolunteerEventDto>(`/api/VolunteerEvents/${id}/publish`);
    return response.data;
  },

  // Cancel a draft/published event
  cancelEvent: async (id: string): Promise<VolunteerEventDto> => {
    const response = await api.post<VolunteerEventDto>(`/api/VolunteerEvents/${id}/cancel`);
    return response.data;
  },

  // Get the registration roster for an event (optionally including cancelled/removed rows)
  getRoster: async (id: string, includeCancelled = false): Promise<RosterResponse> => {
    const response = await api.get<RosterResponse>(`/api/VolunteerEvents/${id}/roster`, {
      params: includeCancelled ? { includeCancelled: true } : undefined,
    });
    return response.data;
  },

  // Remove a registrant on their behalf; promotes the oldest waitlisted volunteer if a confirmed spot frees
  removeRegistrant: async (eventId: string, registrationId: string, reason?: string): Promise<RemoveRegistrantResponse> => {
    const response = await api.post<RemoveRegistrantResponse>(
      `/api/VolunteerEvents/${eventId}/roster/${registrationId}/remove`,
      { reason: reason || undefined },
    );
    return response.data;
  },

  // Activity log for an event, newest first
  getActivity: async (id: string): Promise<ActivityResponse> => {
    const response = await api.get<ActivityResponse>(`/api/VolunteerEvents/${id}/activity`);
    return response.data;
  },

  // Download the roster CSV (cookie auth rides along on same-site navigation)
  getRosterExportUrl: (id: string): string => `${BASE_URL}/api/VolunteerEvents/${id}/roster/export`,
};

export const userClaimsApi = {
  // Get all available feature claims
  getAvailableClaims: async (): Promise<AvailableClaimDto[]> => {
    const response = await api.get<AvailableClaimDto[]>('/api/Users/claims/available');
    return response.data;
  },

  // Search for a user by email
  searchUserByEmail: async (email: string): Promise<UserWithClaimsDto> => {
    const response = await api.get<UserWithClaimsDto>('/api/Users/search', { params: { email } });
    return response.data;
  },

  // Get a user's current claims
  getUserClaims: async (uid: string): Promise<UserWithClaimsDto> => {
    const response = await api.get<UserWithClaimsDto>(`/api/Users/${uid}/claims`);
    return response.data;
  },

  // Set/update claims for a user
  setUserClaims: async (uid: string, claims: Record<string, boolean>): Promise<UserWithClaimsDto> => {
    const request: SetClaimsRequest = { claims };
    const response = await api.put<UserWithClaimsDto>(`/api/Users/${uid}/claims`, request);
    return response.data;
  },

  // Remove a specific claim from a user
  removeUserClaim: async (uid: string, claimKey: string): Promise<void> => {
    await api.delete(`/api/Users/${uid}/claims/${claimKey}`);
  },
};

export const observationApi = {
  // Get trail observations with paging and date filtering
  getObservations: async (params?: {
    page?: number;
    pageSize?: number;
    includeDeleted?: boolean;
    from?: string;
    to?: string;
  }): Promise<TrailObservationListResponse> => {
    const response = await api.get<TrailObservationListResponse>('/api/trail-observations', { params });
    return response.data;
  },

  // Get a specific observation
  getObservation: async (id: string): Promise<TrailObservationDto> => {
    const response = await api.get<TrailObservationDto>(`/api/trail-observations/${id}`);
    return response.data;
  },

  // Soft delete an observation (e.g. obviously bogus)
  deleteObservation: async (id: string): Promise<void> => {
    await api.delete(`/api/trail-observations/${id}`);
  },

  // Restore a soft-deleted observation
  restoreObservation: async (id: string): Promise<TrailObservationDto> => {
    const response = await api.post<TrailObservationDto>(`/api/trail-observations/${id}/restore`);
    return response.data;
  },

  // Get observation statistics
  getStats: async (): Promise<TrailObservationStatsDto> => {
    const response = await api.get<TrailObservationStatsDto>('/api/trail-observations/stats');
    return response.data;
  },
};

export default api;